import { Link } from "react-router-dom";
import { Feather, Handshake, Award, Mail, ClipboardList } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/components/site/Seo";
import { assetUrl } from "@/lib/api";
import { Rich, Reveal } from "@/components/site/Rich";

const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const ICONS = [Feather, Handshake, Award];

const Team = ({ a }) => {
  const team = [1, 2, 3, 4].map((n) => ({ n, name: a[`team${n}_name`], role: a[`team${n}_role`], photo: a[`team${n}_photo`] })).filter((t) => t.name?.trim());
  return (
    <div className="flex flex-col sm:flex-row justify-center items-center sm:items-stretch gap-6" data-testid="about-team">
      {team.map((t) => (
        <div key={t.n} className="w-full max-w-[320px] sm:flex-1 rounded-2xl bg-white p-5 text-center shadow-sm" data-testid={`team-member-${t.n}`}>
          {t.photo ? <img src={assetUrl(t.photo)} alt={t.name} className="w-full aspect-square rounded-xl object-cover object-top" data-testid={`team-photo-${t.n}`} />
            : <span className="w-full aspect-square rounded-xl bg-brand text-white font-serif text-5xl font-bold flex items-center justify-center">{initials(t.name)}</span>}
          <p className="mt-5 font-serif text-lg font-bold text-charcoal">{t.name}</p>
          <p className="text-xs uppercase tracking-wider text-brass mt-1">{t.role}</p>
        </div>
      ))}
    </div>
  );
};

export default function About() {
  const { content } = useSite();
  const a = content.about;
  useSeo("about");
  return (
    <div data-testid="about-page">
      <section className="relative bg-charcoal">
        <img src={assetUrl(a.hero_image)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="relative max-w-[1180px] mx-auto px-6 py-20 sm:py-28 text-white">
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl" data-testid="about-title">{a.title}</h1>
          <p className="mt-4 text-white/85 text-base sm:text-lg">{a.subtitle}</p>
        </div>
      </section>
      <section className="max-w-[1180px] mx-auto px-6 py-16 lg:py-20 space-y-12">
        <Reveal><Team a={a} /></Reveal>
        <Reveal delay={0.1}><Rich html={a.intro} className="text-charcoal/80 text-[15px]" data-testid="about-intro" /></Reveal>
      </section>
      <section className="max-w-[1180px] mx-auto px-6 pb-16">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brass mb-8" data-testid="about-eyebrow">{a.eyebrow}</p>
        <div className="divide-y divide-border">
          {[1, 2, 3].map((n, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={n} className="py-10 grid gap-6 md:grid-cols-[auto_1fr]">
                <span className="h-14 w-14 rounded-xl bg-brand text-white flex items-center justify-center"><Icon className="h-6 w-6" /></span>
                <div data-testid={`about-section-${n}`}>
                  <h2 className="font-serif font-bold uppercase text-2xl text-brand tracking-wide">{a[`s${n}_title`]}</h2>
                  <Rich html={a[`s${n}_body`]} className="mt-4 border-l-[3px] border-brass pl-5 text-charcoal/80 text-[15px]" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
      <section className="max-w-[1180px] mx-auto px-6 pb-20">
        <div className="rounded-3xl bg-charcoal text-white p-8 sm:p-12 relative grain overflow-hidden" data-testid="about-cta">
          <h2 className="relative font-serif font-bold text-3xl">{a.cta_title}</h2>
          <p className="relative mt-3 text-white/75 max-w-2xl">{a.cta_text}</p>
          <a href={`mailto:${content.contact.email}`} className="relative mt-3 inline-block text-brass hover:underline" data-testid="about-cta-email">{content.contact.email}</a>
          <div className="relative mt-8 flex flex-wrap gap-3">
            <Link to="/listing-questionnaire" className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-dark px-6 py-3 text-sm font-bold transition-colors" data-testid="about-cta-questionnaire"><ClipboardList className="h-4 w-4" />Listing Questionnaire</Link>
            <a href={`mailto:${content.contact.email}`} className="inline-flex items-center gap-2 rounded-full border-2 border-white hover:bg-white hover:text-charcoal px-6 py-3 text-sm font-bold transition-colors" data-testid="about-cta-email-button"><Mail className="h-4 w-4" />Email Us</a>
          </div>
        </div>
      </section>
    </div>
  );
}
